import { useEffect, useState } from 'react'

import { WorkflowRun, isWorkflowRunTerminal } from '@digital-twins/shared-types'

import { workflowRunsApi } from '@/lib/api'

/**
 * Fetches ONE workflow run by its run token (Step 1c-ii,
 * thread-output-continuity — closes D1).
 *
 * `useWorkflowRun(threadToken)` (`@/lib/workflows/useWorkflowRun.ts`) is
 * THREAD-scoped: it resolves the thread's current run and re-fetches on every
 * thread navigation. A thread with more than one completed run needs to
 * render results for runs that are no longer current — each anchored under
 * its own `workflow-run-complete:<runToken>` announcement turn — and
 * `useWorkflowRun` has no way to ask for an arbitrary past token. This hook
 * is the other half: keyed on a RUN token, not a thread token, read-only (no
 * gate/retry/cancel actions — those stay on `useWorkflowRun` for the live
 * run), and safe to mount once per announcement turn.
 */

/** Why the run could not be read. Mirrors `WorkflowRunError` from `useWorkflowRun.ts`
 *  (kept local rather than imported: that hook's helpers are not exported,
 *  and duplicating a two-branch status check costs less than widening its
 *  surface for a read-only sibling). */
export interface RunResultsError {
  kind: 'payment_required' | 'request_failed'
  message: string
}

export interface UseRunResultsResult {
  run: WorkflowRun | null
  isLoading: boolean
  error: RunResultsError | null
}

const PAYMENT_REQUIRED_MESSAGE = 'Workflow runs need an active subscription.'
const REQUEST_FAILED_MESSAGE = 'This run could not be loaded.'

function readHttpStatus(cause: unknown): number | null {
  if (typeof cause !== 'object' || cause === null) {
    return null
  }
  const response = (cause as { response?: { status?: unknown } }).response
  if (!response || typeof response.status !== 'number') {
    return null
  }
  return response.status
}

function toRunResultsError(cause: unknown): RunResultsError {
  if (readHttpStatus(cause) === 402) {
    return { kind: 'payment_required', message: PAYMENT_REQUIRED_MESSAGE }
  }
  return { kind: 'request_failed', message: REQUEST_FAILED_MESSAGE }
}

/**
 * Module-level, keyed by run token, shared across every `useRunResults`
 * instance on the page.
 *
 * A thread with several announced runs mounts one hook call per announcement
 * turn; two announcements that happen to name the same token (a re-render, or
 * the same run surfaced twice) must share one network call, not issue one
 * each. Storing the PROMISE (not just the resolved value) is what makes two
 * mounts that fire before the first response lands also share it — the
 * second mount's effect calls `fetchRunCached` and gets the same in-flight
 * promise back rather than starting a second request.
 *
 * A failed fetch is evicted so the next mount (or a re-render after the
 * token is passed again) gets a fresh attempt instead of being stuck on a
 * cached rejection forever.
 *
 * A fetch that resolves a TERMINAL run is never evicted: a finished run is an
 * immutable read, so every later mount can share it. A fetch that resolves a
 * non-terminal run IS evicted the moment it lands, because that value is a
 * snapshot of a run still in motion — keeping it would pin the "running" shape
 * under a token whose completion lands later, and nothing else would ever
 * re-fetch it. Eviction rather than a status filter at the call site: the
 * status is unknown before the read, so no caller can decide this for itself.
 */
const runResultsCache = new Map<string, Promise<WorkflowRun>>()

/**
 * Exported for `composeThreadExhibits`'s `useThreadExhibitRuns`, which reads
 * a LIST of run tokens and so cannot call `useRunResults` once per token.
 * It shares this cache rather than keeping its own: a second cache over the
 * same endpoint is exactly how two readers of one run start disagreeing.
 */
export function fetchRunCached(runToken: string): Promise<WorkflowRun> {
  const cached = runResultsCache.get(runToken)
  if (cached) {
    return cached
  }
  const promise = workflowRunsApi
    .get(runToken)
    .then((run) => {
      if (!isWorkflowRunTerminal(run.status)) {
        runResultsCache.delete(runToken)
      }
      return run
    })
    .catch((cause: unknown) => {
      runResultsCache.delete(runToken)
      throw cause
    })
  runResultsCache.set(runToken, promise)
  return promise
}

/**
 * Reads one workflow run by token, lazily.
 *
 * Fetches ONLY when `runToken` is non-null — a `null` token (the common case
 * for a turn that has not resolved its announcement anchor yet, or is not a
 * run-completion turn at all) must never trigger a request on mount.
 */
export function useRunResults(runToken: string | null): UseRunResultsResult {
  const [run, setRun] = useState<WorkflowRun | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<RunResultsError | null>(null)

  useEffect(() => {
    if (!runToken) {
      setRun(null)
      setIsLoading(false)
      setError(null)
      return
    }

    /* Clear before loading. Holding the previous run while the next token
       resolves renders run A's results under run B's announcement for as long
       as the fetch takes, and a reader cannot tell that is what they are
       looking at. */
    let cancelled = false
    setRun(null)
    setIsLoading(true)
    setError(null)

    fetchRunCached(runToken)
      .then((next) => {
        if (cancelled) {
          return
        }
        setRun(next)
        setIsLoading(false)
      })
      .catch((cause: unknown) => {
        if (cancelled) {
          return
        }
        setIsLoading(false)
        setError(toRunResultsError(cause))
      })

    return () => {
      cancelled = true
    }
  }, [runToken])

  return { run, isLoading, error }
}
